import { BellOutlined, CheckOutlined, MinusOutlined, EditOutlined, MailOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, InputRef, Modal, Row, Select, Switch } from 'antd';
import api from 'api';
import { AxiosError } from 'axios';
import UploadingComponent from 'components/UploadingComponent/UploadingComponent';
import NotificationsSettingsModal from 'components/Modals/NotificationsSettingsModal/NotificationsSettingsModal';
import { IUserData } from 'interfaces';
import type { FieldData } from '@rc-component/form/es/interface';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setUserData } from 'store/slices/userDataSlice';
import classes from './ProfileModal.module.scss';
import cn from 'classnames';
import { getTheme } from 'helpers/getTheme';
import VkButton from 'components/LoginButtons/VkButton/VkButton';
import Spinner from 'components/Spinner/Spinner';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import { setUnlinkModalState } from 'store/slices/windowStateSlice';
import { deleteExternalAuth } from 'store/reducers/userDataCreator';
import YandexButton from 'components/LoginButtons/YandexButton/YandexButton';
import { useNotificationSound } from 'hooks/useNotificationSound/useNotificationSound';
import PhoneInput from 'antd-phone-input';

const buildName = window.REACT_APP_BUILD_NAME !== 'REPLACE_REACT_APP_BUILD_NAME' ? window.REACT_APP_BUILD_NAME : process.env.REACT_APP_BUILD_NAME || 'tazadeal';

type Props = {
  open: boolean;
  onCancel: () => void;
};

const ProfileModal = ({ open, onCancel }: Props) => {
  const [form] = Form.useForm();
  const [isDataUpdating, setIsDataUpdating] = React.useState<boolean>(false);
  const [haveChanges, setHaveChanges] = React.useState<boolean>(false);
  const [haveErrors, setHaveErrors] = React.useState<boolean>(false);
  const [isNotificationsModalOpen, setIsNotificationsModalOpen] = React.useState<boolean>(false);
  const [externalLoading, setExternalLoading] = React.useState<boolean>(false);
  const [externalState, setExternalState] = React.useState<any[]>([]);
  
  const { userData: profile } = useAppSelector((state) => state.userData);
  const { dataRoom } = useAppSelector((store) => store.dataRoom);
  const { messageApi, unlinkModalState } = useAppSelector((store) => store.windowState);
  const { enableSound, disableSound } = useNotificationSound();
  
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const ruSegmentQuickAuth = buildName === 'athena' || window.location.origin.includes('test') || window.location.origin.includes('localhost');

  const getExternalAuthInfo = async () => {
    setExternalLoading(true);
    try {
      const resp = await api.getExternalAuth();
      setExternalState(resp.data);
    } catch { } finally {
      setExternalLoading(false);
    }
  }

  const isAlreadyBindWith = (auth_name: string) => externalState.findIndex(el => String(el.service_name)?.toLocaleLowerCase() === auth_name) + 1;

  const languagesValues = [
    { value: 'en', label: <Trans i18nKey='languages.en' /> },
    { value: 'ru', label: <Trans i18nKey='languages.ru' /> },
  ];

  React.useEffect(() => {
    window.scroll(0, 0);
    setFormData();
  }, [profile, form]);

  React.useEffect(() => {
    getExternalAuthInfo();
  }, []);

  const validatorPhone = (_: any, value: any) => {
    if (value?.valid && typeof value?.valid === 'function') {
      // if (valid(true)) return Promise.resolve(); // strict validation
      if (value?.valid()) return Promise.resolve(); // non-strict validation
      return Promise.reject(t('PersonalInfo.incorrectPhone'));
    } else {
      return Promise.resolve();
    }
  };

  const setFormData = () => {    
    form.setFieldsValue({
      email: profile?.email,
      first_name: profile?.first_name,
      last_name: profile?.last_name,
      phone_number: profile?.phone_number,
      date_format: profile?.date_format,
      description: profile?.description,
      tfa_enabled: profile?.tfa_enabled,
      message_sound: localStorage.getItem('alreadyAllowSound') === 'true',
      language: profile?.language || 'en',
    });
    setHaveChanges(false);
  };

  const onFieldsChange = (values: FieldData[], allValues: FieldData[]) => {
    setHaveErrors(!!form.getFieldsError().filter(({ errors }) => errors.length).length);
    const fieldsCompare = allValues.map((field) => {
      if (profile && (field.name[0] === 'first_name' || field.name[0] === 'last_name')) {
        return (profile[field.name as string[0] as keyof IUserData] as string).replace(/^\s+/g, '').replace(/\s+$/g, '') ===
          (field.value as string).replace(/^\s+/g, '').replace(/\s+$/g, '');
      } else if (field.name[0] === 'phone_number') {
        const prevPhone = profile && profile[field.name as string[0] as keyof IUserData];        
        const currentPhone = field.value?.countryCode + field.value?.areaCode + field.value?.phoneNumber;
        return prevPhone === currentPhone;
      } else if (field.name[0] === 'message_sound') {
        const previous = localStorage.getItem('alreadyAllowSound') === 'true' ? 'true' : 'false'
        const current = String(field.value);        
        return previous === current;
      } else {
        return profile && profile[field.name as string[0] as keyof IUserData] === field.value;
      }
    });
    setHaveChanges(fieldsCompare.includes(false));
  };

  const changeForm = (changedValues: any, _: IUserData) => {
    for (let key in changedValues) {
      // if (key === 'phone_number') {
      // }
    }
  };

  const onFinishSubmit = async (values: any) => {
    setIsDataUpdating(true);
    const correctNumberPhone = values.phone_number?.countryCode
      ? values.phone_number?.countryCode + values.phone_number?.areaCode + values.phone_number.phoneNumber
      : values.phone_number;    
    
    const valuesWithRightPhone = {...values, phone_number: correctNumberPhone };
    let isUpdateProfileData = false;

    let previousProfile: any = {};
    profile &&
      Object.keys(valuesWithRightPhone).forEach((field) => {
        if (profile[field as keyof typeof profile] !== valuesWithRightPhone[field] && !['tfa_enabled','message_sound'].includes(field))
          isUpdateProfileData = true;
        previousProfile[field] = profile[field as keyof typeof profile];
      });
      
    try {
      const previousMessageSound = localStorage.getItem('alreadyAllowSound') === 'true' ? 'true' : 'false';      
      if (String(valuesWithRightPhone.message_sound) !== previousMessageSound) {
        valuesWithRightPhone.message_sound
          ? enableSound()
          : disableSound();
      };
      
      valuesWithRightPhone.tfa_enabled !== profile?.tfa_enabled && (await api.setTFAuth(valuesWithRightPhone.tfa_enabled));
      isUpdateProfileData &&
        (await api.uploadUserInfo(
          {
            ...valuesWithRightPhone,
            first_name: valuesWithRightPhone.first_name.replace(/^\s+/g, '').replace(/\s+$/g, ''),
            last_name: valuesWithRightPhone.last_name.replace(/^\s+/g, '').replace(/\s+$/g, ''),
            details: {
              before: previousProfile,
              after: valuesWithRightPhone,
            },
          },
          dataRoom?.id!
        ));
      messageApi?.success(t('PersonalInfo.successUpdate'));
      dispatch(setUserData({ ...profile!, ...valuesWithRightPhone }));
    } catch (err) {
      setFormData();
      const error = err as AxiosError<any>;
      messageApi?.error(error.response?.data.detail || t('PersonalInfo.errorUpdate'));
    } finally {
      setIsDataUpdating(false);
    }
  };

  const closeIfPossible = async () => {    
    if (profile?.phone_number && profile?.first_name && profile?.last_name && profile?.email) onCancel()
    else {
      form.setFieldsValue({
        email: profile?.email,
        first_name: profile?.first_name,
        last_name: profile?.last_name,
        phone_number: profile?.phone_number,
      });
      form.validateFields();
      messageApi?.warning(t('PersonalInfo.fillRequiredFields'), 6)
    }
  }

  const bindStatus = {
    true: <CheckOutlined className={classes.iconCheck}/>,
    false:<MinusOutlined className={classes.iconClose}/>
  };

  const deleteExternalAuthHandle = async () => {
    await dispatch(deleteExternalAuth({externalState, t, setExternalState}));
  };
  
  return (
    <>
      <Modal
        okText={t('Users.HelperMenu.save')}
        cancelText={t('Users.HelperMenu.cancel')}
        centered
        onCancel={closeIfPossible}
        open={open}
        title={t('Users.HelperMenu.personalInfo')}
        className={cn(classes.card, getTheme())}
        onOk={form.submit}
        okButtonProps={{
            disabled: !haveChanges || haveErrors
        }}
      >
        <Form
          onFieldsChange={onFieldsChange}
          layout='vertical'
          onFinish={onFinishSubmit}
          onValuesChange={changeForm}
          form={form}
          initialValues={{ phone_number: profile?.phone_number }}
        >
          <Row justify={'space-between'}>
            <Col span={11}>
              <Form.Item
                name='first_name'
                className={classes.form_item}
                label={t('PersonalInfo.firstname')}
                rules={[
                  { required: true },
                  {
                    pattern: new RegExp(/^[a-zA-Zа-яА-ЯёЁ\s-]+$/i),
                    message: t('PersonalInfo.onlyLetters'),
                  },
                  {
                    pattern: new RegExp(/^(?!\s)(?!.*\s$)/),
                    message: t('PersonalInfo.ban-space-firstandlast'),
                  },
                  {
                    pattern: new RegExp(/^(?!-)(?!.*-$)/),
                    message: t('PersonalInfo.ban-firstandlast'),
                  },
                  {
                    pattern: new RegExp(/^(?!.*\s{2,})/),
                    message: t('PersonalInfo.onespace'),
                  },
                ]}
              >
                <Input prefix={<UserOutlined />} />
              </Form.Item>
            </Col>
            <Col span={11}>
              <Form.Item
                name='last_name'
                className={classes.form_item}
                label={t('PersonalInfo.lastname')}
                rules={[
                  { required: true, message: t('PersonalInfo.lastnameValidate') },
                  {
                    pattern: new RegExp(/^[a-zA-Zа-яА-ЯёЁ\s-]+$/i),
                    message: t('PersonalInfo.onlyLetters'),
                  },
                  {
                    pattern: new RegExp(/^(?!\s)(?!.*\s$)/),
                    message: t('PersonalInfo.ban-space-firstandlast'),
                  },
                  {
                    pattern: new RegExp(/^(?!-)(?!.*-$)/),
                    message: t('PersonalInfo.ban-firstandlast'),
                  },
                  {
                    pattern: new RegExp(/^(?!.*\s{2,})/),
                    message: t('PersonalInfo.onespace'),
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            className={classes.form_item}
            name='phone_number'
            label={t('PersonalInfo.phone')}
            rules={[
              {
                required: true,
                message: `${t('PersonalInfo.input')}"${t('PersonalInfo.phone')}"`,
              },
              { validator: validatorPhone }
            ]}
          >
            <PhoneInput enableSearch country='ru' placeholder={t('PersonalInfo.phone')}/>
          </Form.Item>


          <Form.Item
            className={classes.form_item}
            name='email'
            label={t('PersonalInfo.email')}
            rules={[{ type: 'email' }, { required: true }]}
          >
            <Input
              disabled            
              prefix={<MailOutlined />}
            />
          </Form.Item>

          {ruSegmentQuickAuth &&
            <>
              <div>{t('login.external.btns.add')}</div>
              <Row className={classes.externalGroup}>
                <div className={classes.btnWrap}>
                  <VkButton
                    action={Boolean(isAlreadyBindWith('vk')) ? 'delete' : 'add'}
                    loading={externalLoading}
                  />
                </div>
                <div className={classes.iconWrap}>
                  {externalLoading
                    ? <Spinner size={22}/>
                    : bindStatus[String(Boolean(isAlreadyBindWith('vk'))) as keyof typeof bindStatus]
                  }
                </div>
              </Row>
              <Row className={classes.externalGroup}>
                <div className={classes.btnWrap}>
                  <YandexButton
                    action={Boolean(isAlreadyBindWith('yandex')) ? 'delete' : 'add'}
                    loading={externalLoading}
                  />
                </div>
                <div className={classes.iconWrap}>
                  {externalLoading
                    ? <Spinner size={22}/>
                    : bindStatus[String(Boolean(isAlreadyBindWith('yandex'))) as keyof typeof bindStatus]
                  }
                </div>
              </Row>
            </>
          }

          <Row className={classes.notificationSettingsButton}>
            <Button
              type='default'
              icon={<BellOutlined />}
              onClick={() => setIsNotificationsModalOpen(true)}
              block
            >
              {t('notificationsSettings.title')}
            </Button>
          </Row>
          <Row className={classes.rowElem}>
            <Form.Item
              labelCol={{ span: 16 }}
              wrapperCol={{ span: 8 }}
              labelAlign='left'
              layout='horizontal'
              className={classes.form_item}
              name='tfa_enabled'
              label={t('PersonalInfo.tfa_enabled')}
              valuePropName='checked'
            >
              <Switch checkedChildren={t('Settings.terms.enable')} unCheckedChildren={t('Settings.terms.disable')} />
            </Form.Item>
          </Row>
          <Row className={classes.rowElem}>
            <Form.Item
              labelCol={{ span: 16 }}
              wrapperCol={{ span: 8 }}
              // colon={false}
              labelAlign='left'
              layout='horizontal'
              className={classes.form_item}
              name='message_sound'
              label={t('PersonalInfo.message_sound')}
              valuePropName='checked'
            >
              <Switch checkedChildren={t('Settings.terms.enable')} unCheckedChildren={t('Settings.terms.disable')} />
            </Form.Item>
          </Row>
          <Row className={classes.rowElem}>
            <Form.Item
              labelCol={{ span: 16 }}
              wrapperCol={{ span: 8 }}
              labelAlign='left'
              layout='horizontal'
              className={classes.form_item}
              name='language'
              label={t('PersonalInfo.emailLanguage')}
              rules={[{ required: true }]}
            >
              <Select options={languagesValues} />
            </Form.Item>
          </Row>
          <Row className={classes.rowElem}>
            <Form.Item
              labelCol={{ span: 16 }}
              wrapperCol={{ span: 8 }}
              labelAlign='left'
              layout='horizontal'
              className={classes.form_item}
              name='date_format'
              label={t('PersonalInfo.date_format.title')}
              rules={[{ required: true }]}
            >
              <Select              
                options={[
                  { label: t('PersonalInfo.date_format.usa'), value: 'usa' },
                  { label: t('PersonalInfo.date_format.international'), value: 'international' },
                ]}
              />
            </Form.Item>
          </Row>

          <div className={classes.descWrap}>
            <Form.Item className={classes.form_item} name='description' label={t('PersonalInfo.about')}>
              <Input.TextArea
                autoSize={{ minRows: 3 }}
                maxLength={500}
                showCount
              />
            </Form.Item>
            <EditOutlined className={classes.ico}/>
          </div>
        </Form>
      
        {isDataUpdating && <UploadingComponent />}
      </Modal>

      <NotificationsSettingsModal
        open={isNotificationsModalOpen}
        onClose={() => setIsNotificationsModalOpen(false)}
        mode='profile'
      />

      {
        unlinkModalState.open && (
          <React.Suspense fallback={<div />}>
            <ConfirmationModal
              isOpen={unlinkModalState.open}
              onCancel={() => dispatch(setUnlinkModalState({open: false, service_name: '', in_progress: false}))}
              isLoading={unlinkModalState?.in_progress}
              centered
              submit={deleteExternalAuthHandle}
              text={(<div>
                <div>{t('login.external.btns.confirmDeleting')} <b>{t('login.external.btns.register', {name: t(`login.external.btns.${unlinkModalState.service_name}`)})}</b></div>
                <div>{t('login.external.btns.confirmDeleting?')}</div>
              </div>)}
              title={t('login.external.btns.confirmDeleteTitle')}
              okBtnDanger={true}
              cancelText={t('main.cancelButton')}
            />
          </React.Suspense>
        )
      }
    </>
  );
};

export default ProfileModal;
